import { useQuery, UseQueryResult } from "@tanstack/react-query";
import { authorizedAxios, useAuth } from "@moonscape/shared";

type UseUkLossWriteoffsOptions = {
  taxYear: number;
  enabled?: boolean;
};

export type UkLossWriteoffsResponse = {
  taxYear: number;
  taxSetting: any;
  lossWriteoffs: any[];
};

export const useUkLossWriteoffs = ({
  taxYear,
  enabled = true,
}: UseUkLossWriteoffsOptions): UseQueryResult<UkLossWriteoffsResponse | null, Error> => {
  const { getToken, user } = useAuth();

  const fetchLossWriteoffs = async (): Promise<UkLossWriteoffsResponse | null> => {
    const token = await getToken();
    try {
      const response = await authorizedAxios(token).get(
        "/export/tax-report/uk/loss-writeoffs",
        {
          params: { year: taxYear },
          validateStatus: (status) =>
            (status >= 200 && status < 300) || status === 204,
        }
      );

      if (response.status === 204) {
        return null;
      }

      return response.data;
    } catch (error: any) {
      const status = error?.response?.status;
      const code = error?.response?.data?.error;
      if (status === 400 && code === "unsupported_jurisdiction") {
        return null;
      }
      throw error;
    }
  };

  return useQuery({
    queryKey: ["uk_loss_writeoffs", taxYear],
    queryFn: fetchLossWriteoffs,
    enabled: enabled && !!user?.email && Number.isFinite(taxYear),
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });
};
